<template>
	<SafeHtml
		:class="iconClasses"
		tag="span"
		profile="svg"
		role="img"
		:aria-label="name"
		:style="iconStyle"
		:html="resolvedSvg"
	/>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from 'vue';
import { IconName, IconNameOrString, IconType } from './Icon.model';
import { Color, Size } from '@arritech/shared/types';
import { iconLoaders } from './Icon.data';
import { SafeHtml } from '../SafeHtml';

defineOptions({
	name: 'Icon',
});

const props = withDefaults(
	defineProps<{
		name: IconNameOrString;
		size?: Size;
		color?: Color;
	}>(),
	{
		size: Size.MEDIUM,
	}
);

const resolvedSvg = ref<string>('');

const loadIcon = async (iconName: IconNameOrString) => {
	const key = iconName as IconType;
	const loader = iconLoaders[key] || iconLoaders[IconName.CROSS];

	if (!loader) {
		resolvedSvg.value = '';
		console.warn('[Icon] No loader found for icon', iconName);
		return;
	}

	try {
		resolvedSvg.value = await loader();
	} catch {
		resolvedSvg.value = '';
		console.warn('[Icon] Failed to load icon', iconName);
	}
};

watch(
	() => props.name,
	(newName) => loadIcon(newName),
	{ immediate: true }
);

const iconClasses = computed(() => [
	'ar-icon',
	`ar-icon--${props.size}`,
	props.name ? `ar-icon--${props.name}` : '',
]);

const iconStyle = computed(() =>
	props.color ? { color: `var(--color-${props.color})` } : {}
);
</script>

<style lang="scss">
.ar-icon {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 1em;
	height: 1em;

	svg {
		width: 1em;
		height: 1em;
		display: block;
		path,
		circle,
		rect,
		polygon,
		line,
		polyline {
			fill: currentColor;
		}
	}

	&--xsmall {
		font-size: 0.6em;
	}
	&--small {
		font-size: 0.8em;
	}
	&--medium {
		font-size: 1em;
	}
	&--large {
		font-size: 1.25em;
	}
	&--xlarge {
		font-size: 1.5em;
	}
}
</style>
